import { useMemo } from 'react';

import { Heading, HStack, type HStackProps } from '@frontend/components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type {
  CascaderOption,
  CascaderCommonProps,
  CascaderSubmenu,
} from '../types';

export interface SubmenuHeaderProps<OptionType extends CascaderOption>
  extends HStackProps {
  submenu: CascaderSubmenu<OptionType>;
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

export const InternalSubmenuHeader = <OptionType extends CascaderOption>({
  submenu,
}: {
  submenu: CascaderSubmenu<OptionType>;
}) => {
  const { cascaderStyles, getCommonProps } = useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomSubmenuHeader = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.SUBMENU_HEADER),
    [getComponent],
  );

  return (
    <CustomSubmenuHeader
      getCommonProps={getCommonProps}
      __css={cascaderStyles.submenuHeader}
      submenu={submenu}
      className="cascader-submenu-header"
    />
  );
};

export const SubmenuHeader = <OptionType extends CascaderOption>({
  submenu,
  getCommonProps: _,
  ...rest
}: SubmenuHeaderProps<OptionType>) => {
  return (
    <HStack {...rest}>
      <Heading
        size="sm"
        as="span"
        flexGrow={1}
        textAlign="left"
        color="feature.secondary"
        textTransform="uppercase"
      >
        {submenu.label}
      </Heading>
    </HStack>
  );
};
